import { formatDate } from '@betterment/core';
import { Box } from '@betterment/samba-react';
import { getLegacySearchParam } from '@betterment/shared-components';
import { get as _get, sortBy as _sortBy } from 'es-toolkit/compat';
import { useSearchParams } from 'react-router-dom';

import { useAccount } from './AccountProvider';
import AccountSetupChecklist from './AccountSetupChecklist';
import AccountTask from './AccountTask';
import CheckingLayout from './CheckingLayout';
import { useCustomer } from './CustomerProvider';
import { LockedBanner } from './LockedBanner';
import Manage from './Manage';
import SummaryTransactions from './SummaryTransactions';
import { useTransactions } from './TransactionsContext';

import Ellipsis from '../../Shared/components/Ellipsis';
import { isOlderAccount } from '../models/account';
import { isDenied, isReissuedPending, isSystemLocked, isVerified } from '../models/card';

const Summary: React.FC = () => {
  const { customer } = useCustomer();
  const { account } = useAccount();

  const [searchParams] = useSearchParams();
  const showOpenSpendingAccountChangeModal = getLegacySearchParam(
    searchParams,
    'show_open_spending_account_change_modal'
  );

  const {
    errors: transactionErrors,
    loadData: loadTransactionData,
    isLoading: isLoadingTransactions,
    transactions,
    // @ts-ignore: Update js-runtime QueryParams to allow for any value type
  } = useTransactions(account.id);

  const accounts = _sortBy(customer.accounts, 'createdAt');
  const isFirstAccount = accounts[0].id === account.id;
  const card = _get(customer, 'card');
  const cardPendingVerification = _get(customer, 'cardPendingVerification', null);
  const isCardSystemLocked = card && isSystemLocked(card);
  const isCardVerified = card && isVerified(card);
  const hasCardError = !card || isDenied(card);
  const hasExistingTransactions = transactions !== null && transactions.length > 0;
  const isAccountOrCustomerLocked = customer.isLocked || account.isLocked;

  const isCardNotReissuedWithTransactions =
    card && account && (!isReissuedPending(card) || !isOlderAccount(account) || !hasExistingTransactions);

  const hasTransactionsAvailable = transactions != null && !isLoadingTransactions && !transactionErrors;
  const shouldDisplayAccountSetupChecklist =
    isFirstAccount &&
    !isCardSystemLocked &&
    hasTransactionsAvailable &&
    isCardNotReissuedWithTransactions &&
    !isAccountOrCustomerLocked &&
    (!isCardVerified || !hasExistingTransactions);
  const cardExpectedDeliveryAt = card ? formatDate(card.expectedDeliveryAt, 'shortOrdinal') : <Ellipsis />;
  const shouldShowLockedBanner = isCardSystemLocked || isAccountOrCustomerLocked;

  return (
    <CheckingLayout title="Overview">
      {shouldShowLockedBanner && (
        <CheckingLayout.Banner>
          <LockedBanner accountLocked={isAccountOrCustomerLocked} cardLocked={isCardSystemLocked} />
        </CheckingLayout.Banner>
      )}
      <CheckingLayout.Slot>
        {shouldDisplayAccountSetupChecklist && (
          <Box
            css={{
              marginBottom: 'space.100',
              paddingTop: 'space.100',

              display: {
                base: 'block',
                sm: 'block',
                md: 'none',
              },
            }}
          >
            <AccountSetupChecklist
              isCardVerified={isCardVerified}
              hasCardError={hasCardError}
              cardExpectedDeliveryAt={cardExpectedDeliveryAt}
              hasExistingTransactions={hasExistingTransactions}
            />
          </Box>
        )}
        <Box
          css={{
            marginBottom: {
              base: 'space.100',
              sm: 'space.100',
              md: 'space.400',
            },
          }}
        />
        <AccountTask mb={[3, 3, 6]} />
        <SummaryTransactions
          errors={transactionErrors}
          loadData={loadTransactionData}
          isLoading={isLoadingTransactions}
          transactions={transactions}
        />
      </CheckingLayout.Slot>
      <CheckingLayout.Slot>
        {shouldDisplayAccountSetupChecklist && (
          <Box
            css={{
              marginBottom: 'space.200',

              display: {
                base: 'none',
                sm: 'none',
                md: 'block',
              },
            }}
          >
            <AccountSetupChecklist
              isCardVerified={isCardVerified}
              hasCardError={hasCardError}
              cardExpectedDeliveryAt={cardExpectedDeliveryAt}
              hasExistingTransactions={hasExistingTransactions}
            />
          </Box>
        )}
        <Box
          css={{
            marginBottom: 'space.200',
          }}
        >
          <Manage
            card={card}
            cardPendingVerification={cardPendingVerification}
            account={account}
            isAccountOrCustomerLocked={isAccountOrCustomerLocked}
            showOpenSpendingAccountChangeModal={showOpenSpendingAccountChangeModal === 'true'}
          />
        </Box>
      </CheckingLayout.Slot>
    </CheckingLayout>
  );
};

export default Summary;
